/**
 * PlayScene.ts — Active gameplay scene; owns engine lifecycle for one level.
 * No React imports. No Skia imports. Orchestrates writes to gameState singleton.
 * @module PlayScene
 */

import { initGameState, gameState, type LevelRuntime } from '../engine/gameState';
import { setSceneUpdateHook, stepPhysics } from '../engine/physicsStep';
import { setActiveStep } from '../engine/sceneStep';
import { resetAccumulator } from '../engine/physicsFrame';
import { resetProjectiles } from '../engine/projectileSystem';
import { WEAPON_AMMO } from '../engine/weaponPickupStep';
import { clearParticles } from '../engine/particleSystem';
import { resetHitEffectVfx } from '../engine/hitEffectVfx';
import { updateHudSnapshot } from '../engine/hudSnapshot';
import { setDeathRespawnHook } from '../engine/respawn';
import { clearAllInputState } from '../input/inputState';
import { CheckpointSystem, type Checkpoint } from '../levels/CheckpointSystem';
import { spawnEnemies } from './playSceneSpawns';
import { FinaleSequence } from './FinaleSequence';
import { setFinaleColumns } from './finaleState';
import { restoreCheckpointFromSave } from './playSceneRestore';
import {
  takeCheckpointSnapshot,
  restoreCheckpointSnapshot,
  type CheckpointSnapshot,
} from './playSceneCheckpoint';
import { populateRuntimeCollectibles } from './playSceneCollectibles';
import { getUserSetting } from '../services/userSettings';
import type {
  StarSeedData,
  ConstellationShardData,
  HeartFragmentData,
  WeaponPickupData,
} from '../types/level';
import type { EnemySpawnData } from '../levels/TileMapLoader';

/**
 * PlayScene — lifecycle controller for one level session.
 * Call `init()` before the first frame and `destroy()` on unmount.
 */
export class PlayScene {
  /** Whether init() has been called and destroy() has not yet been called. */
  private _active = false;
  /** Checkpoint system for this level session — initialized in init(). */
  private _checkpoints: CheckpointSystem | null = null;
  /** Finale cinematic controller — null if this level has no finale gate. */
  private _finale: FinaleSequence | null = null;
  // ── Stored spawn data for death-respawn enemy/collectible reset ───────────
  /** Enemy spawn definitions — stored so enemies can be re-spawned on death. */
  private _enemySpawns: EnemySpawnData[] = [];

  /** Last checkpoint snapshot — null before the first snapshot is taken. */
  private _snapshot: CheckpointSnapshot | null = null;
  /** Monotonic counter — detects stale async callbacks across init/destroy cycles. */
  private _initToken = 0;
  /** Level ID for this session. */
  private _levelId: string = '';

  /**
   * Initialise the scene for the given level.
   * Resets gameState, clears the physics accumulator, and marks the scene active.
   *
   * @param level        - Level geometry to load.
   * @param spawnX       - Nova's starting X position (logical px).
   * @param spawnY       - Nova's starting Y position (logical px).
   * @param levelId      - Unique level identifier for checkpoint persistence.
   * @param checkpoints  - Checkpoint definitions for this level.
   * @param starSeeds    - Star Seed positions from level data.
   * @param shards       - Constellation Shard positions from level data.
   * @param heartFragments - Heart Fragment positions from level data.
   * @param enemySpawns  - Enemy spawn positions from level data.
   * @param finaleConfig - Optional finale gate/ramp config.
   * @param skipCheckpointRestore - When true, do not consult the persisted
   *   `lastCheckpointId` for this level. Used by the in-session Restart button
   *   so the player respawns at the level start rather than the latest
   *   activated checkpoint. The persisted save is left untouched.
   * @param weaponPickups - Weapon pickup spawn positions from level data.
   *   DECISION: Appended after `skipCheckpointRestore` so existing callers
   *   that rely on positional defaults (PlayScene unit tests, future scenes)
   *   continue to compile without changes.
   */
  public init(
    level: LevelRuntime,
    spawnX: number,
    spawnY: number,
    levelId: string,
    checkpoints: Checkpoint[],
    starSeeds: StarSeedData[] = [],
    shards: ConstellationShardData[] = [],
    heartFragments: HeartFragmentData[] = [],
    enemySpawns: EnemySpawnData[] = [],
    finaleConfig?: { gateCol: number; rampCol: number; canSkip: boolean },
    skipCheckpointRestore = false,
    weaponPickups: WeaponPickupData[] = [],
  ): void {
    // DECISION: Return projectiles to pool BEFORE initGameState clears the array.
    resetProjectiles();
    clearParticles();
    // DECISION: Pool is module-scoped; a prior session's burst would otherwise
    // render on this level's first frame.
    resetHitEffectVfx();
    // Clear any latched input flags from a previous session before initializing
    // game state. This prevents held movement/actions from carrying across sessions.
    clearAllInputState();
    initGameState(spawnX, spawnY, level);

    // Store spawn data for death-respawn enemy/collectible reset.
    this._enemySpawns = enemySpawns;
    this._levelId = levelId;
    this._initToken++;

    // DECISION: for...of with push avoids undefined entries from .map() on unknown types.
    gameState.enemies = [];
    this._spawnEnemies(enemySpawns);

    // Populate runtime collectibles + weapon pickups from parsed level data.
    populateRuntimeCollectibles(starSeeds, shards, heartFragments, weaponPickups);

    // DECISION: Write the dev-toggle value at init (not OR-merge) so toggling
    // it OFF re-locks double jump on the next level — initGameState preserves
    // canDoubleJump across init, otherwise the prior `true` would leak forward.
    // The save-driven path below may OR-flip it back on if the save grants it.
    gameState.nova.canDoubleJump = getUserSetting('devUnlockWorld3');
    // Same write-not-OR semantics for wall slide. Persistence trigger is
    // entering World 4 (per AGENTS.md) — not yet shipped, so the dev toggle
    // is the only surfaced path today.
    gameState.nova.canWallSlide = getUserSetting('devUnlockWorld4');
    // DECISION: Seed the (projectileKind, ammo) pair from the dev toggle at
    // scene init so the engine never has to import `services/userSettings`,
    // and so projectileSystem's pre-fire invariant doesn't immediately revert
    // the toggle. The W3 pickup system (Phase 4) writes the same pair on pickup.
    const useGem = getUserSetting('devUseGemShard');
    gameState.projectileKind = useGem ? 'gem_shard' : 'star_shard';
    gameState.ammo = useGem ? WEAPON_AMMO.gem_shard : 0;

    // DECISION: Reset accumulator to avoid spurious physics step on first frame.
    resetAccumulator();
    // Sync HUD snapshot from freshly-initialized gameState.
    updateHudSnapshot(
      gameState.nova.health,
      gameState.nova.maxHealth,
      gameState.seeds,
      gameState.shardsCollected,
      gameState.shardsTotal,
      gameState.score,
      gameState.lives,
      gameState.elapsedSeconds,
      gameState.projectileKind,
      gameState.ammo,
    );
    // Initialize checkpoint system with level-specific data.
    this._checkpoints = new CheckpointSystem(levelId, checkpoints);
    // DECISION: The Restart button skips checkpoint restore so the player
    // always respawns at the level start; fresh level entry from the world
    // map still honours the saved checkpoint.
    if (!skipCheckpointRestore) {
      this._restoreCheckpointFromSave();
    }

    // Take initial snapshot so death-before-checkpoint restores to level start.
    this._takeCheckpointSnapshot();

    // Initialize finale sequence if this level has a gate.
    if (finaleConfig) {
      this._finale = new FinaleSequence();
      this._finale.init(finaleConfig.gateCol, finaleConfig.rampCol, finaleConfig.canSkip);
    } else {
      this._finale = null;
      // DECISION: GATE_COL/RAMP_COL are module-scoped state in finaleState.ts
      // and only ever written by FinaleSequence.init(). Without an explicit
      // reset here, a non-finale level loaded after a finale level (e.g. 1-4
      // after 1-3) inherits the prior gate column, and drawExitPortal() then
      // renders the portal + pedestal at the leaked position. Use a negative
      // sentinel so drawExitPortal() can early-return.
      setFinaleColumns(-1, -1);
    }

    // DECISION: Explicitly register the platformer step so a prior SpaceScene
    // (Phase 4) leaves the runtime in a known state when the player returns to
    // a platformer level. Default already points at stepPhysics, but symmetry
    // with destroy() keeps the contract clear.
    setActiveStep(stepPhysics);
    // Register scene update hook so checkpoint checks run each physics tick.
    setSceneUpdateHook((dt) => this.update(dt));
    // Register death-respawn hook so enemies/collectibles reset on death.
    setDeathRespawnHook(() => this.resetToCheckpoint());
    this._active = true;
  }

  /**
   * Per-tick scene update.
   * Called from the physics loop each tick. Handles checkpoint overlap checks,
   * keeps the respawn position in sync, and snapshots state on checkpoint activation.
   *
   * @param dt - The physics timestep in seconds.
   */
  public update(dt: number): void {
    if (!this._active || !this._checkpoints) return;
    // Check for checkpoint overlap and update respawn position.
    this._checkpoints.update(gameState.nova);
    const { x, y } = this._checkpoints.getRespawnPosition(gameState.spawnX, gameState.spawnY);
    gameState.checkpointSpawnX = x;
    gameState.checkpointSpawnY = y;

    // DECISION: Snapshot collectible/score state when a new checkpoint is activated.
    // Comparing checkpoint ID is O(1) and avoids snapshotting every tick.
    const currentCpId = this._checkpoints.activeCheckpointId;
    if (currentCpId !== null && currentCpId !== this._snapshot?.checkpointId) {
      this._snapshot = takeCheckpointSnapshot(currentCpId);
    }

    // Update finale state machine when active.
    if (this._finale?.isActive) {
      this._finale.update(dt);
    }
  }

  /** Tear the scene down. Clears input and marks gameState as not running. */
  public destroy(): void {
    if (!this._active) return;
    this._active = false;
    // Unregister scene update hook so checkpoint checks stop.
    setSceneUpdateHook(null);
    // Reset active step to the default so the next scene starts from a known baseline.
    setActiveStep(null);
    // Unregister death-respawn hook.
    setDeathRespawnHook(null);
    // Clean up finale state machine.
    this._finale?.destroy();
    this._finale = null;
    gameState.isRunning = false;
    clearParticles();
    // DECISION: Clear all held input on destroy — inputState is a module singleton.
    clearAllInputState();
  }

  /**
   * Whether the scene has been initialised and not yet destroyed.
   */
  public get isActive(): boolean {
    return this._active;
  }

  /**
   * Reset enemies (full re-spawn) and collectibles to checkpoint-snapshot state.
   * Called from the death-respawn hook after deathRespawn() repositions Nova.
   *
   * DECISION: Re-spawn ALL enemies on death (clean slate). This means enemies
   * killed before the checkpoint snapshot can be re-killed for score, since the
   * snapshot already includes their kill score. Accepted for Phase 2 simplicity;
   * Phase 4+ should snapshot killed enemy IDs to prevent score farming.
   */
  public resetToCheckpoint(): void {
    gameState.enemies = [];
    this._spawnEnemies(this._enemySpawns);
    if (this._snapshot) restoreCheckpointSnapshot(this._snapshot);
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /**
   * Spawn enemy entities from the provided spawn data into gameState.enemies.
   * Thin wrapper around the shared `spawnEnemies` helper — see
   * playSceneSpawns.ts for the type → constructor dispatch table.
   * @param spawns - Enemy spawn definitions from level data.
   */
  private _spawnEnemies(spawns: EnemySpawnData[]): void {
    spawnEnemies(spawns);
  }

  /** Take a checkpoint snapshot of the current gameState. */
  private _takeCheckpointSnapshot(): void {
    this._snapshot = takeCheckpointSnapshot(this._checkpoints?.activeCheckpointId ?? null);
  }

  /** Load and restore saved checkpoint, gating gameplay until settled. */
  private _restoreCheckpointFromSave(): void {
    if (!this._checkpoints) return;
    const token = this._initToken;
    restoreCheckpointFromSave({
      initToken: token,
      levelId: this._levelId,
      checkpoints: this._checkpoints,
      isActive: () => this._active,
      isCurrentToken: () => this._initToken === token,
      takeSnapshot: () => this._takeCheckpointSnapshot(),
    });
  }
}
